import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Users,
  PoundSterling,
  TrendingUp,
  Download,
  Printer,
  LogOut,
  Megaphone,
  FileText,
  Activity,
  Truck,
  Heart,
  PieChart as PieIcon,
} from "lucide-react";
import ActivityTrailMap from "../components/ActivityTrailMap";
import { ReplayTourButton } from "../components/OnboardingTour";
import CharityDonorCRM from "../components/CharityDonorCRM";
import CharityPaymentsPanel from "../components/CharityPaymentsPanel";
import CharityCampaignInfo from "../components/CharityCampaignInfo";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "7 Days" },
  { value: "month", label: "30 Days" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtNum = (n) => new Intl.NumberFormat("en-GB").format(n || 0);

const fmtRel = (iso) => {
  if (!iso) return null;
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch (_) { return null; }
};

function Kpi({ icon: Icon, label, value, tone = "slate", testId, delta, deltaFormatter, inverse = false }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  let deltaBlock = null;
  if (delta && typeof delta.change === "number") {
    const up = delta.change > 0;
    const same = delta.change === 0;
    const isGood = inverse ? !up : up;
    const color = same ? "text-slate-500" : isGood ? "text-emerald-600" : "text-red-500";
    const arrow = same ? "→" : up ? "↑" : "↓";
    const fmt = deltaFormatter || ((v) => v);
    const pct = delta.changePct == null ? "" : ` (${delta.changePct > 0 ? "+" : ""}${delta.changePct}%)`;
    deltaBlock = (
      <div className={`mt-1 text-[11px] font-medium ${color}`} data-testid={`${testId}-delta`}>
        {arrow} {fmt(Math.abs(delta.change))}{pct} vs prev
      </div>
    );
  }
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {deltaBlock}
    </div>
  );
}

// ── Cancellation reasons (executive-level distribution)
function ReasonsBar({ reasons }) {
  const r = reasons || {};
  const items = [
    { key: "failedPayment", label: "Failed Payment", color: "bg-rose-500", testId: "reason-failed-payment" },
    { key: "donorRequest", label: "Donor Request", color: "bg-amber-500", testId: "reason-donor-request" },
    { key: "compliance", label: "Compliance", color: "bg-indigo-500", testId: "reason-compliance" },
    { key: "other", label: "Other", color: "bg-slate-400", testId: "reason-other" },
  ];
  const total = items.reduce((s, i) => s + (r[i.key] || 0), 0);
  if (total === 0) {
    return (
      <div className="text-sm text-slate-500" data-testid="cancellation-reasons-empty">
        No cancellations in this period.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="cancellation-reasons-distribution">
      <div className="h-3 rounded-full overflow-hidden flex bg-slate-100">
        {items.map((i) => {
          const v = r[i.key] || 0;
          if (v === 0) return null;
          const pct = (v / total) * 100;
          return <div key={i.key} className={i.color} style={{ width: `${pct}%` }} title={`${i.label}: ${v}`} />;
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((i) => {
          const v = r[i.key] || 0;
          const pct = total ? Math.round((v / total) * 100) : 0;
          return (
            <div key={i.key} className="flex items-start gap-2" data-testid={i.testId}>
              <span className={`w-2.5 h-2.5 rounded-sm mt-1 ${i.color}`} />
              <div>
                <div className="text-xs font-medium text-slate-700">{i.label}</div>
                <div className="text-sm font-semibold text-slate-900">{v} <span className="text-xs text-slate-500 font-normal">({pct}%)</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingBoard, setDownloadingBoard] = useState(false);
  const isCharity = user?.role === "charity_user";  // OCI V2.7B: charity-scoped view
  const reportPath = isCharity ? "/charity/report" : "/client/report";
  const [viewMode, setViewMode] = useState("reporting");  // OCI V2.17B: reporting | donors

  useEffect(() => {
    document.title = isCharity ? "Engage — Charity Portal" : "Engage — Client Portal";
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get(`${reportPath}?period=${period}`)
      .then((repRes) => {
        if (cancelled) return;
        setData(repRes.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Failed to load report");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const downloadCsv = () => {
    const token = sessionStorage.getItem("taptrack_token");
    const url = `${API}/client/report.csv?period=${period}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `client-report-${period}-${data?.periodStart || ""}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  };

  const downloadBoardPack = () => {
    const token = sessionStorage.getItem("taptrack_token");
    const bpPeriod = period === "today" || period === "week" ? "month" : period;
    const url = `${API}/board-pack/export?period=${bpPeriod}`;
    setDownloadingBoard(true);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Board pack unavailable");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().slice(0, 10);
        a.download = `board-pack-${stamp}.html`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => setError("Board pack could not be generated. Please try again."))
      .finally(() => setDownloadingBoard(false));
  };

  const printPdf = () => window.print();

  const orgName = data?.organisation?.name || "Client Portal";
  const periodLabel = useMemo(
    () => PERIODS.find((p) => p.value === period)?.label || "7 Days",
    [period]
  );

  const topCampaign = useMemo(() => {
    const arr = data?.campaignSplit || [];
    return arr.length ? arr[0] : null;
  }, [data]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
              Client Reporting Portal
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900" data-testid="client-org-name">
              {orgName}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/operational-record")}
              className="border-emerald-200 text-emerald-700"
              data-testid="op-record-entry-btn"
            >
              <Activity className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Operational Record</span>
            </Button>
            <ReplayTourButton />
            <Button variant="outline" size="sm" onClick={logout} data-testid="client-logout-btn">
              <LogOut className="h-4 w-4 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {isCharity && (
          <div className="flex items-center gap-1 print:hidden" data-testid="charity-view-toggle">
            <Button
              size="sm"
              variant={viewMode === "reporting" ? "default" : "outline"}
              onClick={() => setViewMode("reporting")}
              data-testid="charity-view-reporting"
              className={viewMode === "reporting" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            >
              Reporting
            </Button>
            <Button
              size="sm"
              variant={viewMode === "donors" ? "default" : "outline"}
              onClick={() => setViewMode("donors")}
              data-testid="charity-view-donors"
              className={viewMode === "donors" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            >
              Donor Records
            </Button>
            <Button
              size="sm"
              variant={viewMode === "payments" ? "default" : "outline"}
              onClick={() => setViewMode("payments")}
              data-testid="charity-view-payments"
              className={viewMode === "payments" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            >
              Payments
            </Button>
            <Button
              size="sm"
              variant={viewMode === "campaign-info" ? "default" : "outline"}
              onClick={() => setViewMode("campaign-info")}
              data-testid="charity-view-campaign-info"
              className={viewMode === "campaign-info" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            >
              Campaign Info
            </Button>
          </div>
        )}

        {isCharity && viewMode === "payments" ? (
          <CharityPaymentsPanel />
        ) : isCharity && viewMode === "campaign-info" ? (
          <CharityCampaignInfo />
        ) : isCharity && viewMode === "donors" ? (
          <CharityDonorCRM />
        ) : (
        <>
        {/* Period + export */}
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div className="flex flex-wrap items-center gap-1" data-testid="client-period-tabs">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => setPeriod(p.value)}
                data-testid={`client-period-${p.value}`}
                className={period === p.value ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isCharity && (
            <Button
              size="sm"
              onClick={downloadBoardPack}
              disabled={!data || loading || downloadingBoard}
              data-testid="client-board-pack-btn"
              className="bg-[#15803D] hover:bg-[#166534] text-white"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {downloadingBoard ? "Generating…" : "Generate Board Pack"}
            </Button>
            )}
            {!isCharity && (
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data || loading} data-testid="client-export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" />
              CSV
            </Button>
            )}
            <Button variant="outline" size="sm" onClick={printPdf} disabled={!data || loading} data-testid="client-export-pdf-btn">
              <Printer className="h-4 w-4 mr-1.5" />
              PDF
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="client-error">
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="text-center py-12 text-slate-500" data-testid="client-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-3"></div>
            Loading your report...
          </div>
        )}

        {data && (
          <>
            {/* Print title */}
            <div className="hidden print:block mb-4">
              <h2 className="text-2xl font-bold">{orgName} — {periodLabel}</h2>
              <p className="text-sm text-slate-600">
                {data.periodStart} → {data.periodEnd?.substring(0, 10)}
              </p>
            </div>

            {/* OPERATIONAL RECORD POINTER — narrative + forecast + attention live there */}
            <Card data-testid="client-op-record-pointer" className="border-emerald-200 bg-emerald-50/40">
              <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Activity className="h-5 w-5 text-emerald-700 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Operational Record · the full operational narrative</div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Health · momentum · forecast · what needs attention · 30-second board summary — all in one shared view, the same numbers your agency sees.
                    </p>
                  </div>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate("/operational-record")}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="client-op-record-cta"
                >
                  Open Operational Record
                </Button>
              </CardContent>
            </Card>

            {/* TRUSTEE VISIBILITY — at-a-glance KPIs scoped to this charity */}
            <section data-testid="client-trustee-visibility" className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                Trustee Visibility · at-a-glance KPIs
              </div>
              <div data-testid="client-kpis" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Kpi
                  icon={Megaphone}
                  label="Active Campaigns"
                  value={fmtNum(data.kpis?.activeCampaigns || 0)}
                  tone="slate"
                  testId="kpi-active-campaigns"
                />
                <Kpi
                  icon={Users}
                  label="Active Fundraisers"
                  value={fmtNum(data.kpis?.activeFundraisers || 0)}
                  tone="indigo"
                  testId="kpi-active-fundraisers"
                />
                <Kpi
                  icon={TrendingUp}
                  label="Signups"
                  value={fmtNum(data.kpis?.conversions || 0)}
                  tone="emerald"
                  testId="kpi-conversions"
                  delta={data.deltas?.conversions}
                />
                <Kpi
                  icon={Truck}
                  label="Delivery Success"
                  value={`${data.kpis?.deliverySuccessPct ?? 100}%`}
                  tone="slate"
                  testId="kpi-delivery-success"
                />
                <Kpi
                  icon={Heart}
                  label="Retention Rate"
                  value={`${data.kpis?.retentionRatePct ?? 100}%`}
                  tone="amber"
                  testId="kpi-retention-rate"
                />
                <Kpi
                  icon={PoundSterling}
                  label="Top Campaign"
                  value={topCampaign ? topCampaign.campaignName : "—"}
                  tone="slate"
                  testId="kpi-top-campaign"
                />
              </div>
            </section>

            {/* ATTENTION REQUIRED — analysis-class breakdown unique to the charity view */}
            <section data-testid="client-attention-required" className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                Attention Required · why donors are cancelling
              </div>
              <Card data-testid="client-cancellation-reasons">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieIcon className="h-4 w-4 text-slate-600" />
                    Cancellation Reasons
                    <Badge variant="secondary" className="text-[10px]">AGGREGATE</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ReasonsBar reasons={data.cancellationReasons} />
                </CardContent>
              </Card>
            </section>

            {/* TRUSTEE VISIBILITY — operational coverage heat map */}
            <section data-testid="client-coverage-section" className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                Trustee Visibility · operational coverage
              </div>
              <Card data-testid="client-heat-map">
                <CardContent className="p-0">
                  <ActivityTrailMap fundraisers={[]} defaultMode="heat" hideTrailMode={true} />
                </CardContent>
              </Card>
            </section>

            {/* EVIDENCE — Top Locations + Top Fundraisers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Locations */}
              <Card data-testid="client-top-locations">
                <CardHeader>
                  <CardTitle className="text-base">Top locations</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.topLocations.length === 0 ? (
                    <p className="text-sm text-slate-500">No location data in this period.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500">
                        <tr>
                          <th className="text-left py-1">#</th>
                          <th className="text-left">Location</th>
                          <th className="text-right">Signups</th>
                          <th className="text-right">Est. Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topLocations.map((r, i) => (
                          <tr key={r.locationId} className="border-t border-slate-100">
                            <td className="py-1.5 text-slate-400">{i + 1}</td>
                            <td className="py-1.5 text-slate-800">{r.locationName}</td>
                            <td className="py-1.5 text-right font-mono">{r.signups}</td>
                            <td className="py-1.5 text-right font-mono text-slate-600">{fmtGBP(r.estRevenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* Top Fundraisers */}
              <Card data-testid="client-top-fundraisers">
                <CardHeader>
                  <CardTitle className="text-base">Top fundraisers</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.topFundraisers.length === 0 ? (
                    <p className="text-sm text-slate-500">No fundraiser activity in this period.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500">
                        <tr>
                          <th className="text-left py-1">#</th>
                          <th className="text-left">Fundraiser</th>
                          <th className="text-right">Signups</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topFundraisers.map((r, i) => (
                          <tr key={r.fundraiserId} className="border-t border-slate-100">
                            <td className="py-1.5 text-slate-400">{i + 1}</td>
                            <td className="py-1.5 text-slate-800">{r.fundraiserName}</td>
                            <td className="py-1.5 text-right font-mono">{r.signups}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Campaign split */}
            <Card data-testid="client-campaign-split">
              <CardHeader>
                <CardTitle className="text-base">Campaign split</CardTitle>
              </CardHeader>
              <CardContent>
                {data.campaignSplit.length === 0 ? (
                  <p className="text-sm text-slate-500">No campaign data in this period.</p>
                ) : (
                  <div className="space-y-3">
                    {data.campaignSplit.map((r) => (
                      <div key={r.campaignId}>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700 font-medium">{r.campaignName}</span>
                          <span className="text-slate-500 text-xs">
                            {r.signups} signups · {fmtGBP(r.estRevenue)}
                          </span>
                        </div>
                        <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, r.sharePct)}%` }} />
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{r.sharePct}%</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-400 text-center pt-2" data-testid="client-footer-note">
              Aggregate reporting only · Personal data excluded · Generated {new Date(data.periodEnd).toLocaleString("en-GB")}
            </p>
          </>
        )}
        </>
        )}
      </main>
    </div>
  );
}
